import { useState } from "react"
import { ResourceFile, ResourceType } from "../Interfaces"
import { open_resource_file } from "../API"

type UseResourceCardProps = {
    resource: ResourceFile
    onOpenVideo: (fileName: string) => void
    onDelete: (fileName: string, type: ResourceType) => void
    resourceType: ResourceType
    onResourceRefresh?: () => void
}

export function useResourceCard({
    resource,
    onOpenVideo,
    onDelete,
    resourceType,
    onResourceRefresh
}: UseResourceCardProps) {
    const [isHovered, setIsHovered] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showDescriptionDialog, setShowDescriptionDialog] = useState(false)

    const handlePlayClick = async () => {
        // Changes depending on the type of file
        if (resource.type === 'mp4') {
            onOpenVideo(resource.name)
        } else {
            try {
                await open_resource_file(resource.name, resourceType)
            } catch (error) {
                console.error('Failed to open resource file:', error)
                // Fallback to direct URL if the API fails
                window.open(resource.url, '_blank')
            }
        }
    }

    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation()
        try {
            const response = await fetch(`/api/afs/serve-resource/${resourceType}/${resource.name}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
            })
            
            if (!response.ok) throw new Error('Download failed')
            
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = resource.name
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Failed to download resource file:', error)
            // Fallback to direct download if the API fails
            const link = document.createElement('a')
            link.href = resource.url
            link.download = resource.name
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowDeleteDialog(true)
    }

    const handleConfirmDelete = () => {
        onDelete(resource.name, resourceType)
        setShowDeleteDialog(false)
    }

    const handleCancelDelete = () => {
        setShowDeleteDialog(false)
    }

    const handleEditDescription = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowDescriptionDialog(true)
    }

    const handleCloseDescriptionDialog = () => {
        setShowDescriptionDialog(false)
    }

    const handleDescriptionUpdated = () => {
        setShowDescriptionDialog(false)
        if (onResourceRefresh) {
            onResourceRefresh()
        }
    }

    const handleMouseEnter = () => setIsHovered(true)
    const handleMouseLeave = () => setIsHovered(false)

    return {
        // State
        isHovered,
        showDeleteDialog,
        showDescriptionDialog,
        
        // Event handlers
        handlePlayClick,
        handleDownload,
        handleDelete,
        handleConfirmDelete,
        handleCancelDelete,
        handleEditDescription,
        handleCloseDescriptionDialog,
        handleDescriptionUpdated,
        handleMouseEnter,
        handleMouseLeave
    }
}
